import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";

/**
 * Shape returned by GET /api/v1/receipts
 * We only need the pagination total and item-level AllocatedAmount.
 */
interface ReceiptsApiResponse {
  data: { allocatedAmount: number }[];
  pagination: { totalItems: number };
}

/**
 * Shape returned by GET /api/v1/promises (we only need the pagination total)
 */
interface PromisesApiResponse {
  pagination: { totalItems: number };
}

/**
 * Shape returned by GET /api/v1/collection-cases (we only need the pagination total)
 */
interface CollectionCasesApiResponse {
  pagination: { totalItems: number };
}

/** Reusable type alias for API query parameter values */
type ApiParamValue = string | number | boolean;

export interface TodaysProgressData {
  paymentsAllocated: { count: number; totalAmount: number };
  promisesKept: { count: number };
  casesResolved: { count: number };
}

/** Format a Date as YYYY-MM-DD for API query params */
function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Hook to fetch "Today's Progress" metrics for the dashboard progress bar.
 *
 * In **live** mode it fires three parallel API calls:
 *   1. GET /api/v1/receipts   – allocated today → count + total £
 *   2. GET /api/v1/promises   – fulfilled (Paid) today → count
 *   3. GET /api/v1/collection-cases – closed, optionally by user → count
 *
 * In **demo** mode it derives the same numbers from the global mock-data store.
 *
 * @param selectedUserId  Pass a user ID string when a specific user is selected,
 *                        or `undefined` for "Everyone".
 */
export function useTodaysProgress(selectedUserId?: string) {
  const { apiGet } = useApi();

  // ── Demo data ──────────────────────────────────────────────────────
  const store = getGlobalDataStore();

  const paymentsReceivedToday = store.getReceiptsReceivedToday();
  const promisesKeptToday = store.getPromisesKeptToday();
  const casesResolvedToday = store.getCasesResolvedToday();

  const demoData: TodaysProgressData = {
    paymentsAllocated: {
      count: paymentsReceivedToday.length,
      totalAmount: paymentsReceivedToday.reduce(
        (sum, r) => sum + r.receivedAmount,
        0,
      ),
    },
    promisesKept: {
      count: promisesKeptToday.length,
    },
    casesResolved: {
      count: casesResolvedToday.length,
    },
  };

  // ── Live fetcher ───────────────────────────────────────────────────
  const today = toDateString(new Date());

  return useModeAwareQuery<TodaysProgressData, Error>(
    ["todays-progress", selectedUserId ?? "everyone"],
    async ({ signal }) => {
      // Build per-endpoint params
      const receiptsParams: Record<string, ApiParamValue> = {
        DateFrom: today,
        DateTo: today,
        AllocationStatus: "fully_allocated",
        Page: 1,
        PageSize: 1, // we only need the pagination totalItems + we'll sum from a second call if needed
        SortBy: "receiptDate",
        SortOrder: "desc",
      };
      if (selectedUserId) {
        receiptsParams.CreditControllerId = Number(selectedUserId);
      }

      const promisesParams: Record<string, ApiParamValue> = {
        Status: "Paid",
        DateFrom: today,
        DateTo: today,
        Page: 1,
        PageSize: 1,
        SortBy: "PromiseDate",
        SortOrder: "desc",
      };
      if (selectedUserId) {
        promisesParams.AssignedUserId = Number(selectedUserId);
      }

      const casesParams: Record<string, ApiParamValue> = {
        Status: "closed",
        Page: 1,
        PageSize: 1,
        SortBy: "CreatedAt",
        SortOrder: "desc",
      };
      if (selectedUserId) {
        casesParams.AssignedTo = Number(selectedUserId);
      }

      // Fire all three requests in parallel
      const [receiptsResponse, promisesResponse, casesResponse] =
        await Promise.all([
          apiGet<ReceiptsApiResponse>("/api/v1/receipts", receiptsParams, signal),
          apiGet<PromisesApiResponse>("/api/v1/promises", promisesParams, signal),
          apiGet<CollectionCasesApiResponse>("/api/v1/collection-cases", casesParams, signal),
        ]);

      // For payment totals we need to sum the allocated amounts across all pages.
      // If the total count is small enough (≤ 200) we can fetch them all in one go;
      // otherwise the count alone is still useful and total amount will be approximate.
      let allocatedTotal = 0;
      const totalAllocatedCount = receiptsResponse.pagination.totalItems;

      if (totalAllocatedCount > 0) {
        // Re-fetch with larger page to get amounts for summing
        const fullReceiptsParams = {
          ...receiptsParams,
          PageSize: Math.min(totalAllocatedCount, 200),
        };
        const fullReceipts = await apiGet<ReceiptsApiResponse>(
          "/api/v1/receipts",
          fullReceiptsParams,
          signal,
        );
        allocatedTotal = fullReceipts.data.reduce(
          (sum, r) => sum + (r.allocatedAmount ?? 0),
          0,
        );
      }

      return {
        paymentsAllocated: {
          count: totalAllocatedCount,
          totalAmount: allocatedTotal,
        },
        promisesKept: {
          count: promisesResponse.pagination.totalItems,
        },
        casesResolved: {
          count: casesResponse.pagination.totalItems,
        },
      };
    },
    demoData,
    { staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: false },
  );
}
